// types
import {
	AccountEntityRelationship,
	ActivityType,
	ArticleCategory,
	EntitySubtype,
	EntityType,
	ErrorCause,
	FormName,
	PermissionResource,
	Status,
	TransactionStatus,
	TransactionType,
	UserRole,
} from '../@types';

// i18
import { TFunction } from 'i18next';

export const transactionTooltipKeys: Record<TransactionType, string> = {
	ADVANCE: 'advance',
	CASH_BENEFIT: 'cashBenefit',
	DIRECT: 'directPayment',
	INITIAL: 'payment',
	NON_CASH_BENEFIT: 'nonCashBenefit',
	OPEX: 'operatingExpense',
	PAYMENT: 'payment',
	REIMBURSEMENT: 'reimbursement',
	REPLENISHMENT: 'inflow',
	SPECIAL: 'specialCommitment',
	TRANSFER: 'transfer',
};

export const transactionReceivedTooltipKeys: Partial<Record<TransactionType, string>> = {
	CASH_BENEFIT: 'cashBenefitReceived',
	PAYMENT: 'paymentReceived',
};

export const transactionNotReceivedTooltipKeys: Partial<Record<TransactionType, string>> = {
	CASH_BENEFIT: 'cashBenefitNotReceived',
	PAYMENT: 'paymentNotReceived',
};

export const transactionRevertedTooltipKeys: Record<TransactionType, string> = {
	ADVANCE: 'advanceReverted',
	CASH_BENEFIT: 'cashBenefitReverted',
	DIRECT: 'directPaymentReverted',
	INITIAL: 'paymentReverted',
	NON_CASH_BENEFIT: 'nonCashBenefitReverted',
	OPEX: 'operatingExpenseReverted',
	PAYMENT: 'paymentReverted',
	REIMBURSEMENT: 'reimbursementReverted',
	REPLENISHMENT: 'replenishmentReverted',
	SPECIAL: 'specialCommitmentReverted',
	TRANSFER: 'transferReverted',
};

export const translateActivityType = (type: ActivityType, t: TFunction) => {
	const activityTypeMap: Record<ActivityType, string> = {
		ACCOUNT_CREATED: t('activityTypes.accountCreated'),
		ACCOUNT_UPDATED: t('activityTypes.accountUpdated'),
		ACT_CREATED: t('activityTypes.actCreated'),
		ACT_UPDATED: t('activityTypes.actUpdated'),
		BENEFIT_CREATED: t('activityTypes.benefitCreated'),
		BENEFIT_UPDATED: t('activityTypes.benefitUpdated'),
		CASH_IN_METHOD_ACTIVATED: t('activityTypes.cashInMethodActivated'),
		CASH_IN_METHOD_CREATED: t('activityTypes.cashInMethodCreated'),
		CASH_IN_METHOD_DEACTIVATED: t('activityTypes.cashInMethodDeactivated'),
		CASH_IN_METHOD_DELETED: t('activityTypes.cashInMethodDeleted'),
		CASH_IN_METHOD_UPDATED: t('activityTypes.cashInMethodUpdated'),
		CATEGORY_ACTIVATED: t('activityTypes.categoryActivated'),
		CATEGORY_CREATED: t('activityTypes.categoryCreated'),
		CATEGORY_DEACTIVATED: t('activityTypes.categoryDeactivated'),
		CATEGORY_UPDATED: t('activityTypes.categoryUpdated'),
		COMMENT_CREATED: t('activityTypes.commentCreated'),
		COMPONENT_ACTIVATED: t('activityTypes.componentActivated'),
		COMPONENT_CREATED: t('activityTypes.componentCreated'),
		COMPONENT_DEACTIVATED: t('activityTypes.componentDeactivated'),
		COMPONENT_UPDATED: t('activityTypes.componentUpdated'),
		CONTRACT_CREATED: t('activityTypes.contractCreated'),
		CONTRACT_UPDATED: t('activityTypes.contractUpdated'),
		DOCUMENT_CREATED: t('activityTypes.documentCreated'),
		DOCUMENT_TYPE_CREATED: t('activityTypes.documentTypeCreated'),
		DOCUMENT_TYPE_UPDATED: t('activityTypes.documentTypeUpdated'),
		DOCUMENT_UPDATED: t('activityTypes.documentUpdated'),
		ENTITY_CREATED: t('activityTypes.entityCreated'),
		ENTITY_UPDATED: t('activityTypes.entityUpdated'),
		ENTITY_WALLET_ACTIVATED: t('activityTypes.entityWalletActivated'),
		ENTITY_WALLET_DEACTIVATED: t('activityTypes.entityWalletDeactivated'),
		EXPENSE_TYPE_ACTIVATED: t('activityTypes.expenseTypeActivated'),
		EXPENSE_TYPE_CREATED: t('activityTypes.expenseTypeCreated'),
		EXPENSE_TYPE_DEACTIVATED: t('activityTypes.expenseTypeDeactivated'),
		EXPENSE_TYPE_UPDATED: t('activityTypes.expenseTypeUpdated'),
		FORM_CREATED: t('activityTypes.formCreated'),
		FORM_UPDATED: t('activityTypes.formUpdated'),
		IFR_CREATED: t('activityTypes.ifrCreated'),
		IFR_FINALIZED: t('activityTypes.ifrFinalized'),
		IFR_PLAN_CREATED: t('activityTypes.ifrPlanCreated'),
		IFR_PLAN_UPDATED: t('activityTypes.ifrPlanUpdated'),
		IFR_PLAN_VERSION_APPROVED: t('activityTypes.ifrPlanVersionApproved'),
		IFR_PLAN_VERSION_CREATED: t('activityTypes.ifrPlanVersionCreated'),
		IFR_PLAN_VERSION_FINALIZED: t('activityTypes.ifrPlanVersionFinalized'),
		IFR_PLAN_VERSION_REJECTED: t('activityTypes.ifrPlanVersionRejected'),
		IFR_PLAN_VERSION_UPDATED: t('activityTypes.ifrPlanVersionUpdated'),
		IFR_UPDATED: t('activityTypes.ifrUpdated'),
		ILAP: t('activityTypes.ilap'),
		LOAN_CREATED: t('activityTypes.loanCreated'),
		LOAN_ENABLED: t('activityTypes.loanEnabled'),
		LOAN_UPDATED: t('activityTypes.loanUpdated'),
		PAYMENT_CREATED: t('activityTypes.paymentCreated'),
		PROGRAM_CREATED: t('activityTypes.programCreated'),
		PROGRAM_UPDATED: t('activityTypes.programUpdated'),
		PROJECT_CREATED: t('activityTypes.projectCreated'),
		PROJECT_UPDATED: t('activityTypes.projectUpdated'),
		RECONCILIATION_ITEM_ACTIVATED: t('activityTypes.reconciliationItemActivated'),
		RECONCILIATION_ITEM_CREATED: t('activityTypes.reconciliationItemCreated'),
		RECONCILIATION_ITEM_DEACTIVATED: t('activityTypes.reconciliationItemDeactivated'),
		RECONCILIATION_ITEM_UPDATED: t('activityTypes.reconciliationItemUpdated'),
		TRANSACTION_CONFIRMED: t('activityTypes.transactionConfirmed'),
		TRANSACTION_CREATED: t('activityTypes.transactionCreated'),
		TRANSACTION_REVERTED: t('activityTypes.transactionReverted'),
		TRANSACTION_UPDATED: t('activityTypes.transactionUpdated'),
		USER_ADDED: t('activityTypes.userAdded'),
		USER_CREATED: t('activityTypes.userCreated'),
		USER_REMOVED: t('activityTypes.userRemoved'),
		USER_UPDATED: t('activityTypes.userUpdated'),
	};

	return activityTypeMap[type] || type;
};

export interface TranslateErrorProps {
	cause: Partial<ErrorCause>;
	t: TFunction;
	frame?: 'SUPPLIER' | 'BENEFICIARY';
}

export const translateError = ({ cause, t, frame }: TranslateErrorProps) => {
	const errorMap: Record<ErrorCause['id'], string> = {
		ACCOUNT_BALANCES_NON_EXISTENT: t('error.accountBalancesNonExistent'),
		ACCOUNT_DUPLICATE: t('error.accountDuplicate'),
		ACCOUNT_ID_NON_EXISTENT: t('error.accountIdNonExistent'),
		ACCOUNT_INVALID_ENTITY_TRANSACTIONS: t('error.accountEntityTransactions'),
		ACCOUNT_INVALID_RELATIONSHIP: t('error.default'),
		ACCOUNT_INVALID_TYPE: t('error.default'),
		ACCOUNT_NON_EXISTENT: t('error.accountNonExistent'),
		ACCOUNT_OWNER_ASSOCIATE: t('error.accountOwnerAssociate'),
		ACCOUNT_OWNER_NON_EXISTENT: t('error.default'),
		ACCOUNT_PROJECT_NON_EXISTENT: t('error.accountProjectNonExistent'),
		ACCOUNT_UNAUTHORIZED_GET: t('error.unauthorized'),
		ACCOUNT_UNAUTHORIZED_PATCH: t('error.unauthorized'),
		ACCOUNT_UNAUTHORIZED_POST: t('error.unauthorized'),
		ACT_UNAUTHORIZED_PATCH: t('error.unauthorized'),
		ACT_UNAUTHORIZED_POST: t('error.unauthorized'),
		ACTIVITY_INVALID_TYPE: t('error.default'),
		ACTIVITY_UNAUTHORIZED_GET: t('error.unauthorized'),
		B2B_USER_NOT_PERMITTED: t('error.b2bUserNotPermitted'),
		BENEFIT_NON_EXISTENT: t('error.default'),
		BENEFIT_UNAUTHORIZED_POST: t('error.unauthorized'),
		BLOCKCHAIN_TRANSACTION_FAILED: t('error.default'),
		BULK_UPLOAD_FIELD_EMPTY: t('error.bulkUploadFieldEmpty'),
		BULK_UPLOAD_INCOMPLETE: t('error.default'),
		CASH_IN_METHOD_DEFAULT_CANNOT_DELETE: t('error.default'),
		CASH_IN_METHOD_DEFAULT_CANNOT_UPDATE: t('error.default'),
		CASH_IN_METHOD_NOT_FOUND: t('error.default'),
		CASH_IN_METHOD_UNAUTHORIZED: t('error.unauthorized'),
		CATEGORY_ALLOCATION_CURRENCY_REQUIRED: t('error.allocationCurrencyRequired'),
		CATEGORY_ALLOCATION_EXCEEDS_LOAN_AMOUNT: t('error.allocationExceedsLoanAmount'),
		CATEGORY_ALLOCATION_INVALID_AMOUNT: t('error.allocationInvalidAmount'),
		CATEGORY_ALLOCATION_LOAN_NOT_FOUND: t('error.allocationLoanNotFound'),
		CATEGORY_ALLOCATION_WBG_LOAN_AMOUNT_NOT_FOUND: t('error.allocationLoanNotFound'),
		CATEGORY_DEFAULT_NOT_EDITABLE: t('error.categoryDefaultNotEditable'),
		CATEGORY_NON_EXISTENT: t('error.categoryNonExistent'),
		CATEGORY_NOT_FOUND: t('error.categoryNonExistent'),
		CATEGORY_UNAUTHORIZED_PATCH: t('error.unauthorized'),
		CATEGORY_UNAUTHORIZED_POST: t('error.unauthorized'),
		COMMENT_UNAUTHORIZED_POST: t('error.unauthorized'),
		COMPONENT_ALLOCATION_CURRENCY_REQUIRED: t('error.allocationCurrencyRequired'),
		COMPONENT_ALLOCATION_EXCEEDS_LOAN_AMOUNT: t('error.allocationExceedsLoanAmount'),
		COMPONENT_ALLOCATION_INVALID_AMOUNT: t('error.allocationInvalidAmount'),
		COMPONENT_ALLOCATION_LOAN_NOT_FOUND: t('error.allocationLoanNotFound'),
		COMPONENT_NON_EXISTENT: t('error.componentNonExistent'),
		COMPONENT_UNAUTHORIZED_PATCH: t('error.unauthorized'),
		COMPONENT_UNAUTHORIZED_POST: t('error.unauthorized'),
		CONTRACT_COMMENT_REQUIRED: t('error.default'),
		CONTRACT_DUPLICATE: t('error.contractDuplicate'),
		CONTRACT_ENTITIES_NON_EXISTENT: t('error.default'),
		CONTRACT_ID_NON_EXISTENT: t('error.contractIdNonExistent'),
		CONTRACT_NON_EXISTENT: t('error.contractNonExistent'),
		CONTRACT_UNAUTHORIZED_GET: t('error.unauthorized'),
		CONTRACT_UNAUTHORIZED_POST: t('error.unauthorized'),
		COUNTRY_CODE_DUPLICATE: t('error.default'),
		COUNTRY_CODE_INVALID: t('error.countryCodeInvalid'),
		COUNTRY_DUPLICATE: t('error.default'),
		CRM_CREATE_ANNOTATION_ERROR: t('error.crmCreateAnnotationFailed'),
		CRM_CREATE_CONTACT_ERROR: t('error.crmCreateContactFailed'),
		CRM_CREATE_INCIDENT_ERROR: t('error.crmCreateIncidentFailed'),
		CRM_GENERATE_TOKEN_ERROR: t('error.crmGenerateAccessTokenFailed'),
		CSV_EMPTY_OR_INVALID: t('error.csvFormatInvalid'),
		DESIGNATED_ACCOUNT_HAS_TRANSACTIONS: t('error.designatedAccountHasTransactions'),
		DOCUMENT_CONTRACT_NON_EXISTENT: t('error.documentContractNonExistent'),
		DOCUMENT_DELETE_FAILED: t('error.documentDeleteFailed'),
		DOCUMENT_FILE_NON_EXISTENT: t('error.documentFileNonExistent'),
		DOCUMENT_INSERT_FAIL: t('error.server'),
		DOCUMENT_INVALID_CONTENT_TYPE: t('error.documentInvalidMimeType'),
		DOCUMENT_INVALID_MIME_TYPE: t('error.documentInvalidMimeType'),
		DOCUMENT_TRANSACTION_NON_EXISTENT: t('error.documentTransactionNonExistent'),
		DOCUMENT_TYPE_DUPLICATE: t('error.documentTypeDuplicate', { name: cause?.details?.name }),
		DOCUMENT_TYPE_NAME_NON_EXISTENT: t('error.documentTypeNameNonExistent'),
		DOCUMENT_TYPE_NON_EXISTENT: t('error.documentTypeNonExistent'),
		DOCUMENT_TYPE_UNAUTHORIZED_POST: t('error.unauthorized'),
		DOCUMENT_UNAUTHORIZED_GET: t('error.unauthorized'),
		DOCUMENT_UNAUTHORIZED_POST: t('error.unauthorized'),
		ENTITY_DUPLICATE: t('error.entityDuplicate', { id: cause?.details?.idExternal }),
		ENTITY_DUPLICATE_ID_EXTERNAL: t('error.entityDuplicate'),
		ENTITY_INVALID_TYPE: t('error.entityInvalidType'),
		ENTITY_KYC_UNVERIFIED: t('error.default'),
		ENTITY_LOANS_IDENTICAL: t('error.entityLoansIdentical'),
		ENTITY_PARENT_NON_EXISTENT: t('error.associatedEntityNotSelected'),
		ENTITY_UNAUTHORIZED_GET: t('error.unauthorized'),
		ENTITY_UNAUTHORIZED_PATCH: t('error.unauthorized'),
		ENTITY_UNAUTHORIZED_POST: t('error.unauthorized'),
		ENTITY_WALLET_NON_EXISTENT: t('error.entityWalletNonExistent'),
		ENTITY_WALLET_RELATION_NON_EXISTENT: t('error.entityWalletNonExistent'),
		EXPENSE_TYPE_DUPLICATE: t('error.expenseTypeDuplicate', { name: cause?.details?.name }),
		EXPENSE_TYPE_NAME_NON_EXISTENT: t('error.expenseTypeNameNonExistent'),
		EXPENSE_TYPE_NON_EXISTENT: t('error.expenseTypeNonExistent'),
		EXPENSE_TYPE_UNAUTHORIZED_POST: t('error.unauthorized'),
		EXPORT_NO_DATA: t('error.default'),
		FILE_INVALID_FORMAT: t('error.fileInvalidFormat'),
		FINANCING_SOURCE_NOT_SELECTED: t('error.financingSourceNotSelected'),
		FORM_FIELD_CHECKBOX_INVALID: t('error.formFieldCheckboxInvalid', {
			label: cause?.details?.label,
			options: cause?.details?.options,
		}),
		FORM_FIELD_DATE_INVALID: t('error.formFieldDateInvalid', { label: cause?.details?.label }),
		FORM_FIELD_LABEL_DUPLICATE: t('error.formFieldLabelDuplicate'),
		FORM_FIELD_NUMBER_INVALID: t('error.formFieldNumberInvalid', { label: cause?.details?.label }),
		FORM_FIELD_SELECT_INVALID: t('error.formFieldSelectInvalid', {
			label: cause?.details?.label,
			options: cause?.details?.options,
		}),
		GRAPH_UNAUTHORIZED: t('error.graphUnauthorized'),
		GRAPH_USER_NOT_FOUND: t('error.default'),
		ID_NON_EXISTENT: t('error.idNonExistent'),
		IFR_INVALID_STATUS: t('error.ifrInvalidStatus'),
		IFR_PLAN_CASH_IN_EXCEEDS_LOAN_AMOUNT: t('error.planCashInExceedsLoanAmount'),
		IFR_PLAN_CASH_OUT_CATEGORY_MONTH_EXCEEDS_CASH_IN: t('error.planCashOutCategoryExceedsCashIn'),
		IFR_PLAN_CASH_OUT_COMPONENT_MONTH_EXCEEDS_CASH_IN: t('error.planCashOutComponentExceedsCashIn'),
		IFR_PLAN_CATEGORY_ALLOCATION_EXCEEDED: t('error.planCategoryAllocationExceeded'),
		IFR_PLAN_COMPONENT_ALLOCATION_EXCEEDED: t('error.planComponentAllocationExceeded'),
		IFR_PLAN_DUPLICATE: t('error.ifrPlanDuplicate'),
		IFR_PLAN_FISCAL_YEAR_DUPLICATE: t('error.ifrPlanFiscalYearDuplicate'),
		IFR_PLAN_FISCAL_YEAR_END_NON_EXISTENT: t('error.default'),
		IFR_PLAN_FISCAL_YEAR_START_NON_EXISTENT: t('error.default'),
		IFR_PLAN_INSERT_FAIL: t('error.server'),
		IFR_PLAN_NAME_NON_EXISTENT: t('error.default'),
		IFR_PLAN_NOT_FOUND: t('error.ifrPlanNotFound'),
		IFR_PLAN_PROJECT_NON_EXISTENT: t('error.default'),
		IFR_PLAN_UNAUTHORIZED_GET: t('error.unauthorized'),
		IFR_PLAN_UNAUTHORIZED_PATCH: t('error.unauthorized'),
		IFR_PLAN_UNAUTHORIZED_POST: t('error.unauthorized'),
		IFR_PLAN_VERSION_DATA_DUPLICATE: t('error.default'),
		IFR_PLAN_VERSION_DATA_IMMUTABLE: t('error.ifrPlanVersionDataImmutable'),
		IFR_PLAN_VERSION_DATA_INVALID_FOREIGN_KEY: t('error.ifrPlanVersionDataInvalidForeignKey'),
		IFR_PLAN_VERSION_DATA_INVALID_MONTH: t('error.ifrPlanVersionDataInvalidMonth'),
		IFR_PLAN_VERSION_DATA_INVALID_SINGLE_REFERENCE: t(
			'error.ifrPlanVersionDataInvalidSingleReference'
		),
		IFR_PLAN_VERSION_DATA_LOAN_NON_EXISTENT: t('error.default'),
		IFR_PLAN_VERSION_DRAFT_EXISTS: t('error.ifrPlanVersionDraftExists'),
		IFR_PLAN_VERSION_INSERT_FAIL: t('error.server'),
		IFR_PLAN_VERSION_INVALID_TRANSITION: t('error.default'),
		IFR_PLAN_VERSION_NOT_FINALIZED: t('error.ifrPlanNotFinalized'),
		IFR_PLAN_VERSION_NOT_FOUND: t('error.ifrPlanNotFound'),
		IFR_PLAN_VERSION_PLAN_NON_EXISTENT: t('error.ifrPlanNotFound'),
		IFR_PLAN_VERSION_UNAUTHORIZED_GET: t('error.unauthorized'),
		IFR_PLAN_VERSION_UNAUTHORIZED_PATCH: t('error.unauthorized'),
		IFR_PLAN_VERSION_UNAUTHORIZED_POST: t('error.unauthorized'),
		IFR_UNAUTHORIZED_PATCH: t('error.unauthorized'),
		IFR_UNAUTHORIZED_POST: t('error.unauthorized'),
		INVALID_COLUMN: t('error.default'),
		LOAN_CURRENCY_NOT_FOUND: t('error.loanCurrencyNotFound'),
		LOAN_DISBURSED_GREATER_THAN_AMOUNT: t('error.loanDisbursedAmount'),
		LOAN_DUPLICATE: t('error.loanDuplicate', {
			financier: cause?.details?.financier,
			num: cause?.details?.num,
		}),
		LOAN_ENTITY_DUPLICATE: t('error.loanEntityDuplicate'),
		LOAN_INVALID_STATUS: t('error.default'),
		LOAN_NON_EXISTENT: t('error.loanNonExistent'),
		LOAN_UNAUTHORIZED_GET: t('error.unauthorized'),
		LOAN_UNAUTHORIZED_PATCH: t('error.unauthorized'),
		LOAN_UNAUTHORIZED_POST: t('error.unauthorized'),
		ME_UNAUTHORIZED_GET: t('error.unauthorized'),
		METHOD_NOT_ALLOWED: t('error.server'),
		NON_EXISTENT_CURRENCY: t('error.default'),
		PDF_FAILED: t('error.pdfFailed'),
		PLAN_UNAUTHORIZED_POST: t('error.unauthorized'),
		PRACTICE_DUPLICATE: t('error.practiceDuplicate'),
		PROGRAM_UNAUTHORIZED_PATCH: t('error.unauthorized'),
		PROGRAM_UNAUTHORIZED_POST: t('error.unauthorized'),
		PROJECT_ALREADY_ONBOARDED: t('error.projectAlreadyOnboarded'),
		PROJECT_DUPLICATE: t('error.default'),
		PROJECT_ENTITY_NON_EXISTENT: t('error.projectEntityNonExistent'),
		PROJECT_LOAN_DUPLICATE: t('error.projectLoanDuplicate'),
		PROJECT_LOAN_NON_EXISTENT: t('error.projectLoanNonExistent'),
		PROJECT_NON_EXISTENT: t('error.default'),
		PROJECT_REPORTING_CURRENCY_NOT_FOUND: t('error.reportingCurrencyNotFound'),
		PROJECT_UNAUTHORIZED_GET: t('error.unauthorized'),
		PROJECT_UNAUTHORIZED_POST: t('error.unauthorized'),
		QUEUE_FAILURE: t('error.server'),
		RECONCILIATION_ITEM_DEFAULT_CANNOT_EDIT: t('error.default'),
		RECONCILIATION_ITEM_UNAUTHORIZED_PATCH: t('error.unauthorized'),
		RECONCILIATION_ITEM_UNAUTHORIZED_POST: t('error.unauthorized'),
		REGION_DUPLICATE: t('error.regionDuplicate'),
		REPORT_END_DATE_BEFORE_START_DATE: t('error.reportEndDateBeforeStartDate'),
		REPORT_INVALID_TYPE: t('error.reportInvalidType'),
		TAG_NON_EXISTENT: t('error.default'),
		TAG_TRANSACTIONS_EXIST: t('error.default'),
		TAG_UNAUTHORIZED_DELETE: t('error.unauthorized'),
		TAG_UNAUTHORIZED_PATCH: t('error.unauthorized'),
		TAG_UNAUTHORIZED_POST: t('error.unauthorized'),
		TRANSACTION_ACCOUNTS_SAME: t('error.transactionAccountsSame'),
		TRANSACTION_AMOUNT_NON_EXISTENT: t('error.transactionAmountNonExistent'),
		TRANSACTION_DOCUMENTS_NON_EXISTENT: t('error.documentsNonExistent'),
		TRANSACTION_ENTITY_NOT_FOUND: t('error.default'),
		TRANSACTION_EXCEEDS_CONTRACT: t('error.transactionExceedsContract', {
			contractAmount: cause?.details?.contractAmount,
			transactionAmount: cause?.details?.transactionAmount,
		}),
		TRANSACTION_EXCEEDS_LOAN_AMOUNT: t('error.transactionExceedsLoanAmount', {
			currency: cause?.details?.currency,
			disbursedAmount: cause?.details?.disbursedAmount,
			loanAmount: cause?.details?.loanAmount,
		}),
		TRANSACTION_ID_NON_EXISTENT: t('error.transactionIdNonExistent'),
		TRANSACTION_INSUFFICIENT_FUNDS: t('error.transactionInsufficientFunds'),
		TRANSACTION_INVALID_TYPE: t('error.default'),
		TRANSACTION_PATCH_INCOMPLETE: t('error.transactionsPatchIncomplete'),
		TRANSACTION_RECEIVED_AMOUNT_NON_EXISTENT: t('error.transactionReceivedAmountNonExistent'),
		TRANSACTION_REIMBURSED_EXCEEDS_REIMBURSEMENT: t('error.reimbursementExceeded', {
			totalReimbursed: cause?.details?.totalReimbursed,
			totalReimbursement: cause?.details?.totalReimbursement,
		}),
		TRANSACTION_UNAUTHORIZED_GET: t('error.unauthorized'),
		TRANSACTION_UNAUTHORIZED_PATCH: t('error.unauthorized'),
		TRANSACTION_UNAUTHORIZED_POST: t('error.unauthorized'),
		UNKNOWN: t('error.default'),
		USER_DUPLICATE: t('error.userDuplicate'),
		USER_EMAIL_NON_EXISTENT: t('error.userEmailNonExistent'),
		USER_FIRST_NAME_NON_EXISTENT: t('error.userFirstNameNonExistent'),
		USER_GROUP_INVALID: t('error.userGroupInvalid'),
		USER_INVALID_ROLE: t('error.userInvalidRole'),
		USER_LAST_NAME_NON_EXISTENT: t('error.userLastNameNonExistent'),
		USER_PERMISSION_NON_EXISTENT: t('error.userPermissionNonExistent'),
		USER_PHONE_OR_EMAIL_EMPTY: t('error.userEmailOrPhoneRequired'),
		USER_UNAUTHORIZED_GET: t('error.unauthorized'),
		USER_UNAUTHORIZED_PATCH: t('error.unauthorized'),
		USER_UNAUTHORIZED_POST: t('error.unauthorized'),
		WALLET_CREATION: t('error.server'),
		WALLET_INACTIVE: t('error.walletInactive'),
		WALLET_UPDATE: t('error.server'),
		WITHDRAWAL_APPLICATION_ID_DUPLICATE: t('error.withdrawalApplicationIdDuplicate'),
	};

	let message = t('error.default');

	if (cause?.id) {
		message = errorMap[cause.id] || message;
		if (frame === 'SUPPLIER') {
			const errorMapSupplier: Partial<Record<ErrorCause['id'], string>> = {
				ENTITY_DUPLICATE: t('error.supplierDuplicate', { idExternal: cause?.details?.idExternal }),
				ENTITY_DUPLICATE_ID_EXTERNAL: t('error.supplierDuplicateIdExternal', {
					idExternal: cause?.details?.idExternal,
				}),
			};
			message = errorMapSupplier[cause.id] || message;
		}
		if (frame === 'BENEFICIARY') {
			const errorMapBeneficiary: Partial<Record<ErrorCause['id'], string>> = {
				ENTITY_DUPLICATE: t('error.beneficiaryDuplicate', {
					idExternal: cause?.details?.idExternal,
				}),
				ENTITY_DUPLICATE_ID_EXTERNAL: t('error.beneficiaryDuplicateIdExternal', {
					idExternal: cause?.details?.idExternal,
				}),
			};
			message = errorMapBeneficiary[cause.id] || message;
		}
	}

	return message;
};

export const translateFormName = (name: FormName, t: TFunction) => {
	const formMap: Record<FormName, string> = {
		BENEFICIARY_INDIVIDUAL: t('forms.beneficiaryIndividual'),
		BENEFICIARY_ORGANIZATION: t('forms.beneficiaryOrganization'),
		SUPPLIER: t('forms.supplier'),
	};

	return formMap[name] || name;
};

export const translatePermissionResource = (resource: PermissionResource, t: TFunction) => {
	const resourceMap: Record<PermissionResource, string> = {
		ACCOUNT: t('account'),
		ACT: t('activity'),
		BENEFICIARY: t('beneficiary'),
		BENEFIT: t('benefit'),
		CASH_BENEFIT: t('cashBenefit'),
		CASH_IN_METHOD: t('cashInMethod'),
		CATEGORY: t('category'),
		COMMENT: t('comment'),
		COMPONENT: t('component'),
		CONTRACT: t('contract'),
		DOCUMENT: t('document'),
		ENTITY: t('entity'),
		EXPENSE_TYPE: t('expenseType'),
		FORM: t('form'),
		LOAN: t('loan'),
		NON_CASH_BENEFIT: t('nonCashBenefit'),
		OPEX: t('operatingExpense'),
		PAYMENT: t('payment'),
		PROGRAM: t('program'),
		RECONCILIATION_ITEM: t('reconciliationItem'),
		REIMBURSEMENT: t('reimbursement'),
		REPLENISHMENT: t('inflow'),
		SUPPLIER: t('supplier'),
		TAG: t('tag'),
		TRANSFER: t('transfer'),
		USER: t('user'),
		WITHDRAWAL_APPLICATION: t('withdrawalApplication'),
	};

	return resourceMap[resource] || resource;
};

export const translateRelationship = (relationship: AccountEntityRelationship, t: TFunction) => {
	const relationships: Record<AccountEntityRelationship, string> = {
		ASSOCIATE: t('associate'),
		OWNER: t('owner'),
	};

	return relationships[relationship] || relationship;
};

export const translateRole = (role: UserRole, t: TFunction) => {
	const roles: Record<UserRole, string> = {
		FNFA: t('fnfa'),
		GENERAL: t('general'),
		LIAISON: t('liaison'),
		STAFF: t('staff'),
		TTL: t('taskTeamLeader'),
		WFA: t('wfa'),
	};

	return roles[role] || role;
};

export const translateStatus = (status: Status, t: TFunction) => {
	const statuses: Record<Status, string> = {
		ACTIVE: t('active'),
		APPROVED: t('approved'),
		COMPLETED: t('completed'),
		CREATED: t('created'),
		DEACTIVATED: t('deactivated'),
		DELETED: t('deleted'),
		DISABLED: t('disabled'),
		DONE: t('done'),
		DRAFT: t('draft'),
		ENABLED: t('enabled'),
		ERROR: t('error'),
		FAILED: t('failed'),
		FINALIZED: t('finalized'),
		HIGH: t('high'),
		INACTIVE: t('inactive'),
		INDETERMINATE: t('indeterminate'),
		INVITED: t('invited'),
		IN_PROGRESS: t('inProgress'),
		LOW: t('low'),
		MINIMAL: t('minimal'),
		MODERATE: t('moderate'),
		PENDING: t('pending'),
		READY: t('ready'),
		REJECTED: t('rejected'),
		RETRY: t('retry'),
		REVERTED: t('reverted'),
		REVISION: t('revision'),
		STARTED: t('started'),
		TRIAL: t('trial'),
	};

	return statuses[status] || status;
};

export const translateStatusDescription = (status: TransactionStatus, t: TFunction) => {
	const statusDescriptions: Record<TransactionStatus, string> = {
		COMPLETED: t('status.transaction.completed.description'),
		FAILED: t('status.transaction.failed.description'),
		IN_PROGRESS: t('status.transaction.inProgress.description'),
		PENDING: t('status.transaction.pending.description'),
		REVERTED: t('status.transaction.reverted.description'),
	};

	return statusDescriptions[status] || status;
};

export const translateStatusTooltip = (status: Status, t: TFunction) => {
	const statusTooltips: Partial<Record<Status, string>> = {
		COMPLETED: t('status.transaction.completed.tooltip'),
		FAILED: t('status.transaction.failed.tooltip'),
		IN_PROGRESS: t('status.transaction.inProgress.tooltip'),
		PENDING: t('status.transaction.pending.tooltip'),
		REVERTED: t('status.transaction.reverted.tooltip'),
		REVISION: t('status.ifrPlan.revision.tooltip'),
	};

	return statusTooltips[status] || status;
};

export const translateTransactionType = (type: TransactionType, t: TFunction) => {
	const types: Record<TransactionType, string> = {
		ADVANCE: t('advance'),
		CASH_BENEFIT: t('cashBenefit'),
		DIRECT: t('directPayment'),
		INITIAL: t('initialDeposit'),
		OPEX: t('operatingExpense'),
		NON_CASH_BENEFIT: t('nonCashBenefit'),
		PAYMENT: t('payment'),
		REIMBURSEMENT: t('reimbursement'),
		REPLENISHMENT: t('inflow'),
		SPECIAL: t('specialCommitment'),
		TRANSFER: t('transfer'),
	};

	return types[type] || type;
};

export const translateType = (type: EntityType | EntitySubtype, t: TFunction) => {
	const types: Record<EntityType | EntitySubtype, string> = {
		AUDITOR: t('auditor'),
		BENEFICIARY: t('beneficiary'),
		BORROWER: t('borrower'),
		DONOR: t('donor'),
		INDIVIDUAL: t('individual'),
		INTERMEDIARY: t('intermediary'),
		ORGANIZATION: t('organization'),
		PIU: t('piu'),
		SUB_PIU: t('subPiu'),
		SUPPLIER: t('supplier'),
		TAX_AUTHORITY: t('taxAuthority'),
	};

	return types[type] || type;
};

export const translateLanguage = (languageCode: Intl.Locale['language']) => {
	const languages: Record<Intl.Locale['language'], string> = {
		af: 'Afrikaans',
		am: 'አማርኛ',
		ar: 'العربية',
		az: 'Azərbaycan',
		be: 'Беларуская',
		bg: 'Български',
		bn: 'বাংলা',
		bs: 'Bosanski',
		ca: 'Català',
		cs: 'Čeština',
		cy: 'Cymraeg',
		da: 'Dansk',
		de: 'Deutsch',
		el: 'Ελληνικά',
		en: 'English',
		es: 'Español',
		et: 'Eesti',
		eu: 'Euskera',
		fa: 'فارسی',
		fi: 'Suomi',
		fil: 'Filipino',
		fr: 'Français',
		ga: 'Gaeilge',
		gl: 'Galego',
		gu: 'ગુજરાતી',
		he: 'עברית',
		hi: 'हिन्दी',
		hr: 'Hrvatski',
		hu: 'Magyar',
		hy: 'Հայերեն',
		id: 'Bahasa Indonesia',
		is: 'Íslenska',
		it: 'Italiano',
		ja: '日本語',
		ka: 'ქართული',
		kk: 'Қазақ тілі',
		km: 'ខ្មែរ',
		kn: 'ಕನ್ನಡ',
		ko: '한국어',
		ky: 'Кыргызча',
		lo: 'ລາວ',
		lt: 'Lietuvių',
		lv: 'Latviešu',
		mk: 'Македонски',
		ml: 'മലയാളം',
		mn: 'Монгол',
		mr: 'मराठी',
		ms: 'Bahasa Melayu',
		mt: 'Malti',
		my: 'မြန်မာ',
		nb: 'Norsk bokmål',
		ne: 'नेपाली',
		nl: 'Nederlands',
		nn: 'Norsk nynorsk',
		or: 'ଓଡ଼ିଆ',
		pa: 'ਪੰਜਾਬੀ',
		pl: 'Polski',
		ps: 'پښتو',
		pt: 'Português',
		ro: 'Română',
		ru: 'Русский',
		si: 'සිංහල',
		sk: 'Slovenčina',
		sl: 'Slovenščina',
		sq: 'Shqip',
		sr: 'Српски',
		sv: 'Svenska',
		sw: 'Kiswahili',
		ta: 'தமிழ்',
		te: 'తెలుగు',
		th: 'ไทย',
		tr: 'Türkçe',
		uk: 'Українська',
		ur: 'اردو',
		uz: "O'zbek",
		vi: 'Tiếng Việt',
		zh: '中文（简体）',
		zu: 'IsiZulu',
	};

	return languages[languageCode] || languageCode;
};

export const translateAccountType = (type: string, t: TFunction) => {
	const accountTypeMap: Record<string, string> = {
		DESIGNATED: t('designated'),
		LOCAL: t('local'),
	};

	return accountTypeMap[type] || type;
};
export const translateCategory = (category: ArticleCategory, t: TFunction): string => {
	const categoryMap: Record<ArticleCategory, string> = {
		ACCOUNTS: t('category.accounts'),
		ENTITIES: t('category.entities'),
		PROJECTS: t('category.projects'),
		REPORTS: t('category.reports'),
		SETTINGS: t('category.settings'),
		TRANSACTIONS: t('category.transactions'),
	};

	return categoryMap[category] || category;
};
